import PropTypes from 'prop-types'
import { Component } from 'react'

import authentication from 'api/authentication'
import passwordOutcome from 'components/authentication/passwordOutcome'
import { PasswordRules } from 'components/authentication/PasswordRules'
import Button from 'components/common/Button'
import InputTextField from 'components/common/InputTextField'
import Text from 'components/common/Text'
import toast from 'lib/toast'
import theme from 'theme'
import { messageFromResponseError } from 'utils/strings'

import {
  PanelHeader,
  PanelTitle,
  PanelDetails
} from '../attacks/details/AttackStyles'

export default class ResetPassword extends Component {
  state = {
    password: '',
    confirmPassword: '',
    showResetPassword: false
  }

  handleInput = (prop) => {
    return (event) => {
      this.setState({
        [prop]: event.target.value
      })
    }
  }

  handleResetPassword = () => {
    const { currentPassword, password } = this.state
    return authentication
      .internalResetPassword(currentPassword, password)
      .then(() => {
        toast({
          type: 'success',
          text: 'Password successfully reset.'
        })
      })
      .catch((err) => {
        toast({
          type: 'error',
          text: messageFromResponseError(
            err,
            'There was an issue setting your password.'
          )
        })
      })
  }

  handleToggleShowResetPassword = () => {
    this.setState(
      {
        showResetPassword: !this.state.showResetPassword
      },
      () => {
        if (this.passwordRef) this.passwordRef.focus()
      }
    )
  }

  setPasswordRef = (ref) => {
    this.passwordRef = ref
  }

  render() {
    const {
      state,
      handleInput,
      handleResetPassword,
      handleToggleShowResetPassword,
      setPasswordRef
    } = this

    const { currentPassword, password, confirmPassword, showResetPassword } =
      state

    const isInvalid = passwordOutcome(password, confirmPassword).some(
      (rule) => rule.invalid
    )

    return (
      <div css={{ marginBottom: theme.space[5] }}>
        <PanelHeader>
          <PanelTitle>Reset Password</PanelTitle>
        </PanelHeader>

        <PanelDetails>
          {showResetPassword ? (
            <ResetPasswordForm
              {...{
                currentPassword,
                password,
                confirmPassword,
                handleInput,
                handleResetPassword,
                handleToggleShowResetPassword,
                setPasswordRef,
                isInvalid
              }}
            />
          ) : (
            <a
              data-test-toggle-show-reset-password
              onClick={handleToggleShowResetPassword}
            >
              I want to reset my password.
            </a>
          )}
        </PanelDetails>
      </div>
    )
  }
}

function ResetPasswordForm({
  currentPassword,
  password,
  confirmPassword,
  handleInput,
  handleResetPassword,
  handleToggleShowResetPassword,
  setPasswordRef,
  isInvalid
}) {
  return (
    <div css={{ maxWidth: '500px' }}>
      <InputTextField
        data-test-current-password
        autoComplete="current-password"
        innerRef={setPasswordRef}
        placeholder="Current Password"
        type="password"
        onChange={handleInput('currentPassword')}
      />
      <InputTextField
        data-test-password
        autoComplete="new-password"
        placeholder="New Password"
        type="password"
        onChange={handleInput('password')}
      />
      <InputTextField
        data-test-confirm-password
        placeholder="Confirm New Password"
        type="password"
        onChange={handleInput('confirmPassword')}
      />
      {password && (
        <PasswordRules
          passwordOutcome={passwordOutcome(password, confirmPassword)}
        />
      )}
      <Text
        as="small"
        color="theme.colors.steel[2]"
        css={{ marginBottom: theme.space[3] }}
      >
        After resetting your password you will be required to log back in.
      </Text>
      <Button
        data-test-reset-password
        primary
        disabled={isInvalid || !currentPassword.length}
        onClick={handleResetPassword}
      >
        Confirm Reset Password
      </Button>
      <Button
        data-test-toggle-hide-reset-password
        secondary
        css={{
          marginLeft: theme.space[2]
        }}
        onClick={handleToggleShowResetPassword}
      >
        Cancel
      </Button>
    </div>
  )
}

ResetPasswordForm.propTypes = {
  currentPassword: PropTypes.string,
  password: PropTypes.string,
  confirmPassword: PropTypes.string,
  handleInput: PropTypes.func,
  handleResetPassword: PropTypes.func,
  handleToggleShowResetPassword: PropTypes.func,
  setPasswordRef: PropTypes.func,
  isInvalid: PropTypes.bool
}

ResetPasswordForm.defaultProps = {
  currentPassword: ''
}
